import { useState, useEffect } from 'react'
import Button from '@/components/Button'
import { PageMetadata } from '@/types/page'

export const metadata: PageMetadata = {
  id: 'cookies',
  category: 'messaging',
  categoryName: 'Messaging Widget',
  name: 'Cookie Consent',
  icon: '🍪',
  path: '/messaging/cookies',
  title: 'Zendesk Cookie Consent',
  description: 'GDPR/Cookie Consent Compliance'
}

type CookieRange = 'all' | 'functional' | 'none'

export default function Cookies() {
  const [cookieSetting, setCookieSetting] = useState<CookieRange | null>(null)
  const [widgetHidden, setWidgetHidden] = useState(false)

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Add Zendesk widget script
    const script = document.createElement('script')
    script.id = 'ze-snippet'
    script.src = 'https://static.zdassets.com/ekr/snippet.js?key=e125418a-9466-44d8-9b3f-2ac10e911ea4'
    script.async = true
    document.body.appendChild(script)

    return () => {
      const existing = document.getElementById('ze-snippet')
      if (existing) existing.remove()
    }
  }, [])

  const handleCookieConsent = (range: CookieRange) => {
    if (window.zE) {
      window.zE('messenger:set', 'cookies', range)
      setCookieSetting(range)

      if (range === 'none') {
        setWidgetHidden(true)
      } else {
        setWidgetHidden(false)
      }
    }
  }

  return (
    <>
      <header>
        <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 md:px-8">
          <div className="md:flex md:items-center md:justify-between md:space-x-5">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <h2 className="mt-2 text-xl text-licorice dark:text-white">{metadata.description}</h2>
            </div>
            <div className="justify-stretch mt-6 flex flex-col-reverse space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-y-0 sm:space-x-3 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
              <Button
                as="a"
                href="https://support.zendesk.com/hc/en-us/articles/4408824378650"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookie Policy
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="mt-8 mb-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-base leading-7 text-gray-600 dark:text-white">
              The Zendesk messaging widget uses cookies and storage to function. Use the <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm">zE('messenger:set', 'cookies', range)</code> API to control cookie usage for GDPR/privacy compliance.
            </p>
          </div>

          {/* Cookie Categories Explanation */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-licorice dark:text-white mb-4">Cookie Categories</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-licorice dark:text-white">Essential Cookies</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Required for the widget to function properly.</p>
              </div>
              <div>
                <h4 className="font-medium text-licorice dark:text-white">Functional Cookies</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">Enable enhanced features and personalization.</p>
              </div>
              <div>
                <h4 className="font-medium text-licorice dark:text-white">Analytics Cookies</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Used for visitor path tracking and proactive message analytics. Disabling these will disable:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mt-2 ml-4">
                  <li>Pages viewed (Visitor's path)</li>
                  <li>Proactive messages analytics</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cookie Consent Banner Simulation */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-licorice dark:text-white mb-4">Cookie Consent</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Choose your cookie preference to see how the widget responds:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => handleCookieConsent('all')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  cookieSetting === 'all'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-green-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="font-semibold text-licorice dark:text-white">Accept All</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Essential + Functional + Analytics
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleCookieConsent('functional')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  cookieSetting === 'functional'
                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-yellow-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">⚠️</div>
                  <div className="font-semibold text-licorice dark:text-white">Functional Only</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Essential + Functional
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleCookieConsent('none')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  cookieSetting === 'none'
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/30'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-red-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">🚫</div>
                  <div className="font-semibold text-licorice dark:text-white">Reject All</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Widget will be hidden
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Current Status */}
          {cookieSetting && (
            <div className={`rounded-lg p-4 mb-6 ${
              cookieSetting === 'all'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                : cookieSetting === 'functional'
                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
            }`}>
              <h4 className="font-semibold mb-2">Current Setting: {cookieSetting.toUpperCase()}</h4>
              {cookieSetting === 'all' && (
                <p className="text-sm">All cookies enabled. Full widget functionality including analytics.</p>
              )}
              {cookieSetting === 'functional' && (
                <p className="text-sm">Analytics cookies disabled. Visitor path and proactive message analytics will not work.</p>
              )}
              {cookieSetting === 'none' && (
                <p className="text-sm">Widget is hidden. Local and session storage cleared. No cookies stored.</p>
              )}
            </div>
          )}

          {widgetHidden && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 dark:text-red-200">
                ⚠️ <strong>Widget Hidden:</strong> Setting cookies to "none" hides the widget and clears all storage.
                To restore, select "Accept All" or "Functional Only".
              </p>
            </div>
          )}

          {/* Code Example */}
          <div className="bg-gray-900 rounded-lg p-6 overflow-x-auto">
            <pre className="text-sm text-gray-100">
              <code>{`// Accept all cookies (default)
zE('messenger:set', 'cookies', 'all')

// Accept only functional cookies (no analytics)
zE('messenger:set', 'cookies', 'functional')

// Reject all cookies (hides widget)
zE('messenger:set', 'cookies', 'none')`}</code>
            </pre>
          </div>

          {/* Additional Info */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-semibold text-licorice dark:text-white mb-2">Implementation Notes</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-disc list-inside">
              <li>This API should be called before the widget fully loads for best results</li>
              <li>Setting to "none" will hide the widget completely</li>
              <li>Setting to "functional" disables analytics but keeps core functionality</li>
              <li>Default behavior (no API call) is "all" cookies enabled</li>
              <li>See <a href="https://support.zendesk.com/hc/en-us/articles/4408824378650" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">Zendesk Cookie Policy</a> for details</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}

// Extend window type for Zendesk
declare global {
  interface Window {
    zE?: any
  }
}
